class AppDate extends Date {
  private static shortMonths = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

  private static longMonths = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

  private static shortDays = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

  private static longDays = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

  private replaceChars(chr: string): string | number {
    const key = `${chr}`.trim();

    if (key === 'd') return (this.date < 10 ? '0' : '') + this.date;
    if (key === 'D') return AppDate.shortDays[this.day];
    if (key === 'j') return this.date;
    if (key === 'l') return AppDate.longDays[this.day];
    if (key === 'N') return this.day === 0 ? 7 : this.day;
    if (key === 'S') {
      if (this.date % 10 === 1 && this.date !== 11) return 'st';
      if (this.date % 10 === 2 && this.date !== 12) return 'nd';
      if (this.date % 10 === 3 && this.date !== 13) return 'rd';

      return 'th';
    }
    if (key === 'w') return this.day;
    if (key === 'z') return Math.ceil((this.time - this.startOfYear.time) / 86400000);
    if (key === 'W') return new AppDate(this).week;
    if (key === 'F') return AppDate.longMonths[this.month];
    if (key === 'm') return (this.month < 9 ? '0' : '') + (this.month + 1);
    if (key === 'M') return AppDate.shortMonths[this.month];
    if (key === 'n') return this.month + 1;
    if (key === 't') return new AppDate(this.fullYear + (this.month === 11 ? 1 : 0), this.month === 11 ? 0 : (this.month + 1), 0).date;
    if (key === 'L') return `${(this.fullYear % 400 === 0 || (this.fullYear % 100 !== 0 && this.fullYear % 4 === 0))}`;
    if (key === 'o') return AppDate.from(this.setDate(this.date - ((this.day + 6) % 7) + 3)).fullYear;
    if (key === 'y') return (`${this.fullYear}`).substring(2);
    if (key === 'Y') return this.fullYear;
    if (key === 'a') return this.hours < 12 ? 'am' : 'pm';
    if (key === 'A') return this.hours < 12 ? 'AM' : 'PM';
    if (key === 'B') return Math.floor(((((this.getUTCHours() + 1) % 24) + (this.getUTCMinutes() / 60) + (this.getUTCSeconds() / 3600)) * 1000) / 24);
    if (key === 'g') return this.hours % 12 || 12;
    if (key === 'G') return this.hours;
    if (key === 'h') return ((this.hours % 12 || 12) < 10 ? '0' : '') + (this.hours % 12 || 12);
    if (key === 'H') return (this.hours < 10 ? '0' : '') + this.hours;
    if (key === 'i') return (this.minutes < 10 ? '0' : '') + this.minutes;
    if (key === 's') return (this.seconds < 10 ? '0' : '') + this.seconds;
    if (key === 'v') {
      if (this.milliseconds < 10) return `00${this.milliseconds}`;
      if (this.milliseconds < 100) return `0${this.milliseconds}`;

      return this.milliseconds;
    }
    if (key === 'e') return Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (key === 'I') {
      let DST = null;
      for (let i = 0; i < 12; i += 1) {
        const d = new AppDate(this.fullYear, i, 1);
        const offset = d.getTimezoneOffset();

        if (DST === null) DST = offset;
        else if (offset < DST) { DST = offset; break; } else if (offset > DST) break;
      }

      return ((this.getTimezoneOffset() === DST) as any | 0) ? 1 : 0;
    }
    if (key === 'O') {
      const O = this.timezoneOffset;

      return (-O < 0 ? '-' : '+') + (Math.abs(O / 60) < 10 ? '0' : '') + Math.floor(Math.abs(O / 60)) + (Math.abs(O % 60) === 0 ? '00' : ((Math.abs(O % 60) < 10 ? '0' : '')) + (Math.abs(O % 60)));
    }
    if (key === 'P') {
      const P = this.timezoneOffset;

      return `${(-P < 0 ? '-' : '+') + (Math.abs(P / 60) < 10 ? '0' : '') + Math.floor(Math.abs(P / 60))}:${Math.abs(P % 60) === 0 ? '00' : ((Math.abs(P % 60) < 10 ? '0' : '')) + (Math.abs(P % 60))}`;
    }
    if (key === 'T') {
      const tz = this.toLocaleTimeString(navigator.language, { timeZoneName: 'short' }).split(' ');

      return tz[tz.length - 1];
    }
    if (key === 'Z') return -this.timezoneOffset * 60;
    if (key === 'c') return this.format('Y-m-d\\TH:i:sP');
    if (key === 'r') return this.toString();
    if (key === 'U') return Math.floor(this.time / 1000);

    return key;
  }

  static from(date: Date | AppDate | number): AppDate {
    return new AppDate(date);
  }

  // Simulates PHP's date function
  format(format: string, removeOffset = false) {
    let date = AppDate.from(this);

    if (removeOffset) {
      date = date.subMinutes(Math.abs(date.timezoneOffset));
    }

    return format.replace(/(\\?)(.)/g, (_, esc, chr: string) => ((esc === '' && date.replaceChars(chr)) ? `${date.replaceChars(chr)}` : chr));
  }

  get startOfDay() {
    return new AppDate(this.fullYear, this.month, this.date, 0, 0, 0, 0);
  }

  get startOfWeek(): AppDate {
    let firstDayOfWeek = new AppDate(this);
    const { day } = firstDayOfWeek;
    const diff = firstDayOfWeek.date - day + (day === 0 ? -6 : 1);
    firstDayOfWeek = new AppDate(firstDayOfWeek.setDate(diff));

    return new AppDate(firstDayOfWeek.fullYear, firstDayOfWeek.month, firstDayOfWeek.date, 0, 0, 0, 0);
  }

  get startOfMonth() {
    return new AppDate(this.fullYear, this.month, 1, 0, 0, 0, 0);
  }

  get startOfYear() {
    return new AppDate(this.fullYear, 0, 1, 0, 0, 0, 0);
  }

  get endOfDay() {
    return new AppDate(this.fullYear, this.month, this.date, 23, 59, 59, 999);
  }

  get endOfWeek() {
    return new AppDate(this.fullYear, this.month, this.date + 6, 23, 59, 59, 999);
  }

  get endOfMonth() {
    return new AppDate(this.fullYear, this.month + 1, 0, 23, 59, 59, 999);
  }

  get endOfYear(): AppDate {
    return new AppDate(this.fullYear + 1, 0, 0, 23, 59, 59, 999);
  }

  get week() {
    const date = new AppDate(this.getTime());
    date.setHours(0, 0, 0, 0);
    // eslint-disable-next-line no-mixed-operators
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    const week1 = new AppDate(date.getFullYear(), 0, 4);
    // eslint-disable-next-line no-mixed-operators
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  }

  get weekYear() {
    const date = new AppDate(this.time);
    date.setDate(date.date + 3 - ((date.day + 6) % 7));

    return date.fullYear;
  }

  get utc(): AppDate {
    const date = new AppDate(this);
    date.setMinutes(date.minutes - date.timezoneOffset);

    return date;
  }

  diffInYears(diffTo: AppDate | Date): number {
    const diffToDate = new AppDate(diffTo);

    return Math.abs(diffToDate.fullYear - this.fullYear);
  }

  diffInMonths(diffTo: AppDate | Date): number {
    const diffToDate = new AppDate(diffTo);

    let months = (diffToDate.fullYear - this.fullYear) * 12;
    months -= this.month;
    months += diffToDate.month;

    return Math.abs(months <= 0 ? 0 : months);
  }

  diffInWeeks(diffTo: AppDate | Date): number {
    const diffToDate = new AppDate(diffTo);
    const millisecondsPerDay = 24 * 60 * 60 * 1000;

    return Math.floor(Math.abs((this.utc.milliseconds - diffToDate.utc.milliseconds) / millisecondsPerDay / 7));
  }

  diffInDays(diffTo: AppDate | Date): number {
    const diffToDate = new AppDate(diffTo);
    const millisecondsPerDay = 24 * 60 * 60 * 1000;

    return Math.floor(Math.abs((this.utc.milliseconds - diffToDate.utc.milliseconds) / millisecondsPerDay));
  }

  diffInHours(diffTo: AppDate | Date): number {
    const diffToDate = new AppDate(diffTo);
    const millisecondsPerDay = 60 * 60 * 1000;

    return Math.floor(Math.abs((this.utc.milliseconds - diffToDate.utc.milliseconds) / millisecondsPerDay));
  }

  diffInMinutes(diffTo: AppDate | Date): number {
    const diffToDate = new AppDate(diffTo);
    const millisecondsPerDay = 60 * 1000;

    return Math.floor(Math.abs((this.utc.milliseconds - diffToDate.utc.milliseconds) / millisecondsPerDay));
  }

  diffInSeconds(diffTo: AppDate | Date, floor = true): number {
    const diffToDate = new AppDate(diffTo);
    const millisecondsPerDay = 1000;
    let diff = Math.abs((this.utc.milliseconds - diffToDate.utc.milliseconds) / millisecondsPerDay);

    if (floor) {
      diff = Math.floor(diff);
    }

    return diff;
  }

  diffInMilliSeconds(diffDate: AppDate | Date, floor = true): number {
    const dateToDiff = new AppDate(diffDate);
    let diff = Math.abs((this.utc.milliseconds - dateToDiff.utc.milliseconds));

    if (floor) {
      diff = Math.floor(diff);
    }

    return diff;
  }

  gte(diffDate: AppDate | Date): boolean {
    const dateToDiff = new AppDate(diffDate);
    const diff = this.utc.getTime() - dateToDiff.utc.getTime();

    return diff >= 0;
  }

  lte(diffDate: AppDate | Date): boolean {
    const dateToDiff = new AppDate(diffDate);
    const diff = dateToDiff.utc.getTime() - this.utc.getTime();

    return diff >= 0;
  }

  isInRangeOff(fromDate: AppDate | Date | string, toDate: AppDate | Date | string): boolean {
    const dateFrom = new AppDate(fromDate);
    const dateTo = new AppDate(toDate);

    return this.gte(dateFrom) && this.lte(dateTo);
  }

  addMinutes(minutes = 1) {
    const date = new AppDate(this);
    date.setMinutes(date.minutes + minutes);
    return date;
  }

  addHours(hours = 1) {
    const date = new AppDate(this);
    date.setHours(date.hours + hours);
    return date;
  }

  addDays(days = 1) {
    const date = new AppDate(this);
    date.setDate(date.date + days);
    return date;
  }

  addWeeks(weeks = 1) {
    const date = new AppDate(this);
    date.setDate(date.date + (weeks * 7));
    return date;
  }

  addMonths(months = 1) {
    const date = new AppDate(this);
    date.setMonth(date.month + months);
    return date;
  }

  addYears(years = 1) {
    const date = new AppDate(this);
    date.setFullYear(date.fullYear + years);
    return date;
  }

  subMinutes(minutes = 1) {
    const date = new AppDate(this);
    date.setMinutes(date.minutes - minutes);
    return date;
  }

  subHours(hours = 1) {
    const date = new AppDate(this);
    date.setHours(date.hours - hours);
    return date;
  }

  subDays(days = 1) {
    const date = new AppDate(this);
    date.setDate(date.date - days);
    return date;
  }

  subWeeks(weeks = 1) {
    const date = new AppDate(this);
    date.setDate(date.date - (weeks * 7));
    return date;
  }

  subMonths(months = 1) {
    const date = new AppDate(this);
    date.setMonth(date.month - months);
    return date;
  }

  subYears(years = 1): AppDate {
    const date = new AppDate(this);
    date.setFullYear(date.fullYear - years);
    return date;
  }

  get date(): number {
    return this.getDate();
  }

  get fullYear(): number {
    return this.getFullYear();
  }

  get month(): number {
    return this.getMonth();
  }

  get day(): number {
    return this.getDay();
  }

  get hours(): number {
    return this.getHours();
  }

  get minutes(): number {
    return this.getMinutes();
  }

  get seconds(): number {
    return this.getSeconds();
  }

  get milliseconds(): number {
    return this.getMilliseconds();
  }

  get time(): number {
    return this.getTime();
  }

  get timezoneOffset(): number {
    return this.getTimezoneOffset();
  }

  get isStartOfWeek(): boolean {
    return new AppDate().startOfDay.time === new AppDate(this).startOfWeek.time;
  }

  get isStartOfMonth(): boolean {
    return new AppDate().startOfDay.time === new AppDate(this).startOfMonth.time;
  }

  get isStartOfYear(): boolean {
    return new AppDate().startOfDay.time === new AppDate(this).startOfYear.time;
  }
}

export default AppDate;
